import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import { useAuth } from '../context/AuthContext';
import UserAdminRequestDetailModal from './UserAdminRequestDetailModal';
import '../styles/ProcurementTable.css';

interface Request {
  _id: string;
  company_request_id: string;
  product_name: string;
  product_brand: string;
  quantity: number;
  unit: string;
  budget: number;
  currency: string;
  status: string;
  phase: string;
  company_approval_status: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  user_email: string;
  user_department: string;
  company_id: string;
  company_name: string;
}

interface UserAdminProcurementTableProps {
  onRefresh?: () => void;
}

const UserAdminProcurementTable: React.FC<UserAdminProcurementTableProps> = ({ onRefresh }) => {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<Request[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const itemsPerPage = 20;

  useEffect(() => {
    fetchRequests();
  }, [currentPage, filterStatus]);

  const fetchRequests = async () => {
    if (!token || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });

      if (filterStatus) {
        params.append('status', filterStatus);
      }

      const response = await fetch(`${API_ENDPOINTS.USERADMIN_REQUESTS}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRequests(data.data);
          setTotalPages(data.pagination.pages);
          setTotalRequests(data.pagination.total);
        } else {
          setError(data.error || 'Failed to fetch requests');
        }
      } else {
        setError('Failed to fetch requests');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Error fetching requests:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = (requestId: string) => {
    navigate(`/useradmin/request/${requestId}`);
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedRequestId(null);
  };

  const handleApproveClick = (requestId: string) => {
    setPendingRequestId(requestId);
    setShowApproveConfirm(true);
  };

  const handleApprove = async () => {
    if (!pendingRequestId || !token) return;

    try {
      const response = await fetch(API_ENDPOINTS.USERADMIN_APPROVE_REQUEST(pendingRequestId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(t('common.request_approved_successfully'));
          fetchRequests();
          if (onRefresh) onRefresh();
          setShowApproveConfirm(false);
          setPendingRequestId(null);
        } else {
          alert(data.error || 'Failed to approve request');
        }
      } else {
        alert('Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Network error occurred');
    }
  };

  const handleRejectClick = (requestId: string) => {
    setPendingRequestId(requestId);
    setRejectionReason('');
    setShowRejectConfirm(true);
  };

  const handleReject = async () => {
    if (!pendingRequestId || !token || !rejectionReason.trim()) return;

    try {
      const response = await fetch(API_ENDPOINTS.USERADMIN_REJECT_REQUEST(pendingRequestId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rejection_reason: rejectionReason.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(t('common.request_rejected_successfully'));
          fetchRequests();
          if (onRefresh) onRefresh();
          setShowRejectConfirm(false);
          setPendingRequestId(null);
          setRejectionReason('');
        } else {
          alert(data.error || 'Failed to reject request');
        }
      } else {
        alert('Failed to reject request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Network error occurred');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency === 'TRY' ? '₺' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
    return `${symbol}${amount.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status: string, companyApprovalStatus: string) => {
    let className = 'status-badge';
    let text = status;

    if (companyApprovalStatus === 'waiting') {
      className += ' status-waiting';
      text = t('common.waiting_for_company_approval');
    } else if (companyApprovalStatus === 'approved_by_company') {
      className += ' status-approved';
      text = t('common.approved_by_company');
    } else if (companyApprovalStatus === 'rejected_by_company') {
      className += ' status-rejected';
      text = t('common.rejected_by_company');
    } else {
      className += ' status-default';
    }

    return <span className={className}>{text}</span>;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleStatusFilter = (status: string) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  if (isLoading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="procurement-table-container">
      <div className="table-header">
        <h2>{t('common.requests_pending_company_approval')}</h2>
        <div className="table-controls">
          <select
            value={filterStatus}
            onChange={(e) => handleStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="">{t('common.all_statuses')}</option>
            <option value="phase_1">{t('common.phase_1')}</option>
            <option value="approved">{t('common.approved')}</option>
          </select>
        </div>
      </div>

      <div className="table-info">
        <p>{t('common.showing_results', { 
          start: ((currentPage - 1) * itemsPerPage) + 1, 
          end: Math.min(currentPage * itemsPerPage, totalRequests), 
          total: totalRequests 
        })}</p>
      </div>

      <div className="table-wrapper">
        <table className="procurement-table">
          <thead>
            <tr>
              <th>{t('common.company_request_id')}</th>
              <th>{t('common.product_name')}</th>
              <th>{t('common.product_brand')}</th>
              <th>{t('common.quantity')}</th>
              <th>{t('common.budget')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.user')}</th>
              <th>{t('common.created_date')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan={9} className="no-data">
                  {t('common.no_requests_found')}
                </td>
              </tr>
            ) : (
              requests.map((request) => (
                <tr key={request._id} className="table-row">
                  <td>{request.company_request_id || 'N/A'}</td>
                  <td>{request.product_name}</td>
                  <td>{request.product_brand || 'N/A'}</td>
                  <td>{request.quantity} {request.unit}</td>
                  <td>{formatCurrency(request.budget, request.currency)}</td>
                  <td>{getStatusBadge(request.status, request.company_approval_status)}</td>
                  <td>
                    <div className="user-info">
                      <div className="user-name">{request.user_name}</div>
                      <div className="user-email">{request.user_email}</div>
                      {request.user_department && (
                        <div className="user-department">{request.user_department}</div>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(request.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleRowClick(request._id)}
                      >
                        {t('common.view_details')}
                      </button>
                      <button
                        className="btn btn-approve"
                        onClick={() => handleApproveClick(request._id)}
                      >
                        {t('common.approve')}
                      </button>
                      <button
                        className="btn btn-reject"
                        onClick={() => handleRejectClick(request._id)}
                      >
                        {t('common.reject')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {t('common.previous')}
          </button>
          
          <div className="pagination-info">
            {t('common.page_of', { current: currentPage, total: totalPages })}
          </div>
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <UserAdminRequestDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleDetailModalClose}
        requestId={selectedRequestId}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      {/* Approve Confirmation Modal */}
      {showApproveConfirm && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>{t('common.confirm_approval')}</h3>
            </div>
            <div className="modal-content">
              <p>{t('common.are_you_sure_approve')}</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowApproveConfirm(false);
                  setPendingRequestId(null);
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-approve"
                onClick={handleApprove}
              >
                {t('common.approve')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectConfirm && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>{t('common.confirm_rejection')}</h3>
            </div>
            <div className="modal-content">
              <p>{t('common.are_you_sure_reject')}</p>
              <div className="form-group">
                <label>{t('common.rejection_reason')}</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder={t('common.enter_rejection_reason')}
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectConfirm(false);
                  setPendingRequestId(null);
                  setRejectionReason('');
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-reject"
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
              >
                {t('common.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAdminProcurementTable;
